/**
 * Field-level metadata and value derivations for the operations dashboard's
 * alarm/repair columns — the single table both the live alarms/resolved
 * grids and the CSV export read, keyed by the `resolved` flag (false: the
 * active-alarms shape, true: the resolved/repair shape). Kept JSX-free so
 * the duration math is unit-testable without a bundler.
 *
 * Contains:
 * - computeDuration — the active-vs-resolved duration-ms calculation
 * - buildOperationsFieldData — ordered field metadata (headerName, width,
 *   valueGetter, valueFormatter) for the two column families
 *
 * `formatDateTime`/`formatDuration` are injected by the caller rather than
 * imported here — `utils/formatUtils` pulls in `immutable`'s UMD build,
 * which node's strict-ESM loader can't statically resolve, and this module
 * is exercised directly under `node --test`.
 */

/**
 * Duration in ms from alarm timestamp to resolution. The active family
 * treats a missing `resolved_at` as "still ongoing" (end = now); the
 * resolved family never assumes an implicit "now" endpoint — an unresolved
 * row has no business appearing in a resolved-only feed, so it contributes
 * zero duration rather than a bogus one.
 *
 * @param {boolean} resolved - true for the resolved/repair family.
 * @param {object} row - grid row; reads `timestamp` and `resolved_at`.
 * @returns {number} Duration in milliseconds (0 when inputs are missing).
 */
export function computeDuration(resolved, row) {
  if (!row.timestamp) return 0;
  if (resolved) {
    if (!row.resolved_at) return 0;
    return new Date(row.resolved_at).getTime() - new Date(row.timestamp).getTime();
  }
  const start = new Date(row.timestamp).getTime();
  const end = row.resolved_at ? new Date(row.resolved_at).getTime() : Date.now();
  return end - start;
}

function alarmNameValue(row, tAlarm) {
  const cat = row.category_key;
  if (!cat) return '';
  const label = tAlarm(`alarms.${cat}`, { defaultValue: cat.replace(/_/g, ' ') });
  const count = row.alarm_count || 1;
  return count > 1 ? `${label} x${count}` : label;
}

/**
 * @param {object} opts
 * @param {boolean} opts.resolved - active-alarms shape (false) vs resolved/repair shape (true).
 * @param {{siteGroup: string, site: string}} opts.terms - tenant-term labels for the two header columns.
 * @param {Function} opts.tAlarm - alarm-category translator, `(key, { defaultValue }) => string`.
 * @param {Function} opts.t - i18next translator, for column headers.
 * @param {object} opts.user - current user, threaded into `formatDateTime` for locale formatting.
 * @param {Function} opts.formatDateTime - `(value, user) => string`, from `utils/formatUtils`.
 * @param {Function} opts.formatDuration - `(ms) => string`, from `utils/formatUtils`.
 * @returns {object[]} Ordered column field-metadata (headerName/width/valueGetter/valueFormatter/exportOnly).
 */
export function buildOperationsFieldData({ resolved, terms, tAlarm, t, user, formatDateTime, formatDuration }) {
  const formatTimestamp = value => (value ? formatDateTime(value, user) : '');

  const fields = [
    { field: 'site_group_name', headerName: terms.siteGroup, minWidth: 120, hideable: false },
    { field: 'site_name', headerName: terms.site, flex: 1, minWidth: 120, hideable: false },
    { field: 'structure_type', headerName: t('general.type'), width: 75, sortable: false },
    {
      field: 'faces',
      headerName: t('operationsDashboard.columnFaces'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      hideable: false,
      valueFormatter: value => (Array.isArray(value) ? value.join(', ') : ''),
    },
    {
      field: 'alarm_name',
      headerName: t('status.alarm'),
      flex: 1,
      minWidth: 180,
      hideable: false,
      valueGetter: (_value, row) => alarmNameValue(row, tAlarm),
    },
  ];

  if (!resolved) {
    fields.push({ field: 'status', headerName: t('general.status'), width: 100, sortable: false, hideable: false });
  }

  fields.push(
    {
      field: 'duration',
      headerName: t('operationsDashboard.columnDuration'),
      width: 110,
      ...(resolved ? { sortable: false } : {}),
      valueGetter: (_value, row) => computeDuration(resolved, row),
      valueFormatter: value => formatDuration(value),
    },
    {
      field: 'duration_minutes',
      headerName: t('operationsDashboard.columnDurationMinutes'),
      exportOnly: true,
      valueGetter: (_value, row) => Math.round(computeDuration(resolved, row) / 60000),
    },
    {
      field: 'timestamp',
      headerName: resolved ? t('alarms.triggered') : t('general.timestamp'),
      width: 180,
      valueFormatter: formatTimestamp,
    },
  );

  if (resolved) {
    fields.push({
      field: 'resolved_at',
      headerName: t('operationsDashboard.resolved'),
      width: 180,
      valueFormatter: formatTimestamp,
    });
  }

  return fields;
}
